<template>
  <div
    v-if="show"
    class="upload-modal-overlay"
    @click.self="handleOverlayClick"
  >
    <div class="upload-modal">
      <div class="upload-modal-header">
        <h3>{{ modalTitle }}</h3>
        <button class="close-btn" @click="closeModal" :disabled="uploading && !uploadSuccess">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>

      <div class="upload-modal-body">
        <!-- Success State -->
        <div v-if="uploadSuccess" class="upload-success-state">
          <div class="success-icon">
            <i class="bi bi-check-circle-fill"></i>
          </div>
          <div class="success-text">{{ successMessage }}</div>
          <button
            v-if="uploadedMaterialId"
            class="btn btn-outline-primary mt-3"
            @click="goToMaterial"
          >
            <i class="bi bi-arrow-right me-2"></i>
            {{ $t('upload.go_to_material') }}
          </button>
        </div>

        <!-- Upload State (not success) -->
        <template v-else>
          <!-- Drop Zone -->
          <div
            class="drop-zone"
            :class="{
              'drop-zone-active': isDragging,
              'drop-zone-disabled': uploading,
              'drop-zone-has-file': selectedFile && !uploading
            }"
            @dragover.prevent="handleDragOver"
            @dragleave.prevent="handleDragLeave"
            @drop.prevent="handleDrop"
            @click="triggerFileInput"
          >
            <input
              ref="fileInput"
              type="file"
              accept="audio/*,video/*"
              @change="handleFileSelect"
              style="display: none"
            />

            <template v-if="!selectedFile && !uploading">
              <div class="drop-zone-icon">
                <i class="bi bi-cloud-arrow-up"></i>
              </div>
              <div class="drop-zone-text">
                <span class="drop-zone-main">{{ $t('upload.drop_here') }}</span>
                <span class="drop-zone-hint">{{ $t('upload.or_click') }}</span>
              </div>
              <div class="drop-zone-formats">
                {{ $t('upload.supported_formats') }}
              </div>
            </template>

            <template v-else-if="selectedFile && !uploading">
              <div class="selected-file">
                <div class="file-icon">
                  <i :class="fileIcon"></i>
                </div>
                <div class="file-info">
                  <div class="file-name">{{ selectedFile.name }}</div>
                  <div class="file-size">{{ formatFileSize(selectedFile.size) }}</div>
                </div>
                <button class="remove-file-btn" @click.stop="removeFile">
                  <i class="bi bi-x-circle"></i>
                </button>
              </div>
            </template>

            <template v-else>
              <div class="upload-progress">
                <!-- File info during upload -->
                <div class="uploading-file-info">
                  <div class="file-icon">
                    <i :class="fileIcon"></i>
                  </div>
                  <div class="file-details">
                    <div class="file-name">{{ selectedFile.name }}</div>
                    <div class="file-size">{{ formatFileSize(selectedFile.size) }}</div>
                  </div>
                </div>

                <!-- Progress bar with percentage -->
                <div class="progress-section">
                  <div class="progress-bar-container">
                    <div class="progress-bar" :style="{ width: uploadProgress + '%' }"></div>
                  </div>
                  <div class="progress-info">
                    <span class="progress-status">
                      {{ isPaused ? $t('upload.paused') : $t('upload.uploading') }}
                    </span>
                    <span class="progress-percent">{{ uploadProgress }}%</span>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <!-- File Title -->
          <div class="form-group" v-if="selectedFile && !uploading">
            <label for="fileTitle">{{ $t('upload.title_label') }}</label>
            <input
              id="fileTitle"
              v-model="fileTitle"
              type="text"
              class="form-control"
              :placeholder="$t('upload.title_placeholder')"
            />
          </div>

          <!-- Error Message -->
          <div v-if="errorMessage" class="error-message">
            <i class="bi bi-exclamation-triangle"></i>
            {{ errorMessage }}
          </div>
        </template>
      </div>

      <div class="upload-modal-footer">
        <!-- Success: only Close button -->
        <template v-if="uploadSuccess">
          <button class="btn btn-primary" @click="handleSuccessClose">
            <i class="bi bi-check-lg me-2"></i>
            {{ $t('common.close') }}
          </button>
        </template>

        <!-- Uploading: Pause/Resume + Cancel -->
        <template v-else-if="uploading">
          <button
            class="btn btn-outline-secondary"
            @click="abortUpload"
          >
            <i class="bi bi-x-lg me-2"></i>
            {{ $t('common.cancel') }}
          </button>
          <button
            v-if="!isPaused && !canResume"
            class="btn btn-secondary"
            @click="pauseUpload"
          >
            <i class="bi bi-pause-fill me-2"></i>
            {{ $t('upload.pause') }}
          </button>
          <button
            v-if="isPaused || canResume"
            class="btn btn-primary"
            @click="resumeUpload"
          >
            <i class="bi bi-play-fill me-2"></i>
            {{ $t('upload.resume') }}
          </button>
        </template>

        <!-- Normal: Cancel + Upload -->
        <template v-else>
          <button
            class="btn btn-secondary"
            @click="closeModal"
          >
            {{ $t('common.cancel') }}
          </button>
          <button
            class="btn btn-primary"
            @click="uploadFile" v-track="{ event: 'upload.submit.click', target: 'file' }"
            :disabled="!canUpload"
          >
            <i class="bi bi-cloud-upload me-2"></i>
            {{ $t('upload.button') }}
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axiosConfig from '@/lib/axiosConfig';
import ChunkedUploader from '@/lib/chunked-uploader.js';
import { API_URL } from '@/config.js';
import { mapGetters } from 'vuex';
import { trackGoal, isTrackingEnabled } from '@/lib/attribution';

export default {
  name: 'UploadFileModal',

  props: {
    show: {
      type: Boolean,
      default: false
    }
  },

  emits: ['close', 'uploaded'],

  data() {
    return {
      isDragging: false,
      selectedFile: null,
      fileTitle: '',
      uploading: false,
      uploadProgress: 0,
      errorMessage: '',
      successMessage: '',
      uploadSuccess: false,
      uploadedMaterialId: null,
      uploadedQueueId: null,
      // Непустой = сервер принял загрузку как гостевую (user_id=0). Кабинетные
      // маршруты такому материалу недоступны, ему нужна гостевая страница статуса.
      uploadedQueueToken: null,
      maxFileSize: 4 * 1024 * 1024 * 1024, // 4GB (chunked upload supports large files)
      chunkedThreshold: 0, // Always use chunked upload for resume capability
      chunkedUploader: null,
      isPaused: false,
      canResume: false,
      autoRetryCount: 0,
      maxAutoRetries: 3
    };
  },

  computed: {
    ...mapGetters('app', ['configData']),

    serviceName() {
      return this.configData?.service_name || this.configData?.chatbot_name || 'vid2text';
    },

    modalTitle() {
      if (this.serviceName === 'clipcut' || this.serviceName === 'narezkavideo') {
        return this.$t('upload.title_clipcut');
      } else if (this.serviceName === 'vid2text') {
        return this.$t('upload.title_vid2text');
      }
      return this.$t('upload.title');
    },

    canUpload() {
      return this.selectedFile && !this.uploading && !this.successMessage;
    },

    fileIcon() {
      if (!this.selectedFile) return 'bi bi-file-earmark';

      const type = this.selectedFile.type;
      if (type.startsWith('video/')) return 'bi bi-file-earmark-play';
      if (type.startsWith('audio/')) return 'bi bi-file-earmark-music';
      return 'bi bi-file-earmark';
    }
  },

  watch: {
    show(val) {
      if (val) {
        this.resetState();
      }
    }
  },

  methods: {
    resetState() {
      this.selectedFile = null;
      this.fileTitle = '';
      this.uploading = false;
      this.uploadProgress = 0;
      this.errorMessage = '';
      this.successMessage = '';
      this.uploadSuccess = false;
      this.uploadedMaterialId = null;
      this.uploadedQueueId = null;
      this.uploadedQueueToken = null;
      this.isDragging = false;
      this.isPaused = false;
      this.canResume = false;
      this.autoRetryCount = 0;

      // Abort any ongoing chunked upload
      if (this.chunkedUploader) {
        this.chunkedUploader.abort();
        this.chunkedUploader = null;
      }
    },

    closeModal() {
      if (!this.uploading || this.uploadSuccess) {
        this.$emit('close');
      }
    },

    handleOverlayClick() {
      if (!this.uploading || this.uploadSuccess) {
        this.closeModal();
      }
    },

    handleSuccessClose() {
      this.$emit('close');
    },

    goToMaterial() {
      // Гость: материал не привязан к аккаунту, /article и /meeting ему ничего не
      // покажут. Ведём на страницу статуса по queue_token — там же email-разлочка
      // и «забрать результат в Telegram».
      if (this.uploadedQueueToken) {
        this.$router.push(`/processing/${this.uploadedQueueToken}`);
        this.$emit('close');
        return;
      }
      if (this.uploadedMaterialId) {
        if (this.serviceName === 'meetscribe' && this.uploadedQueueId) {
          const hash16 = String(this.uploadedMaterialId).substring(0, 16);
          this.$router.push(`/meeting/${this.uploadedQueueId}_${hash16}`);
        } else {
          this.$router.push(`/article/${this.uploadedMaterialId}`);
        }
        this.$emit('close');
      }
    },

    triggerFileInput() {
      if (!this.uploading) {
        this.$refs.fileInput.click();
      }
    },

    handleDragOver() {
      if (!this.uploading) {
        this.isDragging = true;
      }
    },

    handleDragLeave() {
      this.isDragging = false;
    },

    handleDrop(e) {
      this.isDragging = false;
      if (this.uploading) return;

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        this.processFile(files[0]);
      }
    },

    handleFileSelect(e) {
      const files = e.target.files;
      if (files.length > 0) {
        this.processFile(files[0]);
      }
    },

    processFile(file) {
      this.errorMessage = '';

      // Validate file type
      if (!file.type.startsWith('audio/') && !file.type.startsWith('video/')) {
        this.errorMessage = this.$t('upload.error_invalid_type');
        return;
      }

      // Validate file size
      if (file.size > this.maxFileSize) {
        this.errorMessage = this.$t('upload.error_too_large', { size: '4GB' });
        return;
      }

      this.selectedFile = file;
      // Auto-fill title from filename (without extension)
      this.fileTitle = file.name.replace(/\.[^/.]+$/, '');
    },

    removeFile() {
      this.selectedFile = null;
      this.fileTitle = '';
      this.errorMessage = '';
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    async uploadFile() {
      if (!this.selectedFile) return;

      try {
        const configData = this.$store.state.app?.configData || {};
        const chatbotName = configData.chatbot_name || configData.service_name || '';
        if (isTrackingEnabled(chatbotName) && configData.metrika_id) {
          trackGoal(configData.metrika_id, 'upload_started', {
            file_size: this.selectedFile.size,
          });
        }
      } catch (e) { /* noop */ }

      // Use chunked upload for large files
      if (this.selectedFile.size > this.chunkedThreshold) {
        return this.uploadChunked();
      }

      // Simple upload for smaller files
      return this.uploadSimple();
    },

    async uploadSimple() {
      this.uploading = true;
      this.uploadProgress = 0;
      this.errorMessage = '';

      const formData = new FormData();
      formData.append('action', 'upload_file');
      formData.append('file', this.selectedFile);
      formData.append('title', this.fileTitle || this.selectedFile.name);

      try {
        const response = await axiosConfig.post(API_URL, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            }
          }
        });

        const data = response.data;

        if (data.success) {
          this.handleUploadSuccess(data);
        } else {
          this.errorMessage = data.error || this.$t('upload.error_generic');
          this.uploading = false;
        }
      } catch (error) {
        console.error('Upload error:', error);
        this.errorMessage = this.$t('upload.error_generic');
        this.uploading = false;
      }
    },

    async uploadChunked() {
      this.uploading = true;
      this.uploadProgress = 0;
      this.errorMessage = '';
      this.isPaused = false;
      this.canResume = false;

      // Get auth token from localStorage (same as axios interceptor)
      const authToken = localStorage.getItem('authToken');
      const authHeader = authToken ? `Bearer ${authToken}` : '';

      // Create ChunkedUploader instance
      // chatbot_name must be in URL query params for backend auth (initChatBotTokens)
      const endpointWithChatbot = `${API_URL}?chatbot_name=${encodeURIComponent(this.serviceName)}`;
      this.chunkedUploader = new ChunkedUploader({
        endpoint: endpointWithChatbot,
        chunkSize: 5 * 1024 * 1024, // 5MB chunks
        maxFileSize: this.maxFileSize,
        maxRetries: 3,
        retryDelay: 1000,
        headers: {
          'Authorization': authHeader
        },

        onProgress: (percent) => {
          this.uploadProgress = Math.round(percent);
        },

        onChunkComplete: (chunkIndex, totalChunks) => {
          // Reset auto-retry counter on successful chunk
          this.autoRetryCount = 0;
          console.log(`Chunk ${chunkIndex + 1}/${totalChunks} uploaded`);
        },

        onComplete: (response) => {
          if (response.success) {
            this.handleUploadSuccess(response);
          } else {
            // Server returned a 200 with success:false (often with empty .error).
            // Without this log it's a silent dead-end — frontend just shows error_generic.
            const errStr = response.error || '<empty>';
            this.logUploadError({ message: 'complete success:false err="' + errStr + '"' });
            this.errorMessage = response.error || this.$t('upload.error_generic');
            this.uploading = false;
          }
          this.chunkedUploader = null;
        },

        onError: (error, canResume) => {
          console.error('Chunked upload error:', error);
          this.logUploadError(error);

          if (canResume && this.autoRetryCount < this.maxAutoRetries) {
            // Auto-retry with increasing delay
            this.autoRetryCount++;
            const delay = this.autoRetryCount * 3000; // 3s, 6s, 9s
            console.log(`Auto-retry ${this.autoRetryCount}/${this.maxAutoRetries} in ${delay}ms...`);
            this.errorMessage = this.$t('upload.retrying') || `Reconnecting... (attempt ${this.autoRetryCount}/${this.maxAutoRetries})`;
            setTimeout(() => {
              if (this.chunkedUploader && !this.chunkedUploader.isAborted) {
                this.errorMessage = '';
                this.chunkedUploader.resume().catch(() => {
                  // Error will trigger onError again
                });
              }
            }, delay);
          } else {
            // All auto-retries exhausted, show error to user
            this.errorMessage = error.message || this.$t('upload.error_generic');
            this.canResume = canResume;
            if (!canResume) {
              this.uploading = false;
            }
          }
        },

        onPause: () => {
          this.isPaused = true;
        },

        onResume: () => {
          this.isPaused = false;
          this.errorMessage = '';
        }
      });

      const metadata = {
        title: this.fileTitle || this.selectedFile.name,
        chatbot_name: this.serviceName
      };

      try {
        await this.chunkedUploader.upload(this.selectedFile, metadata);
      } catch (error) {
        // Error already handled in onError callback
        if (!this.canResume) {
          this.chunkedUploader = null;
        }
      }
    },

    handleUploadSuccess(data) {
      // Set success message based on service
      if (this.serviceName === 'clipcut' || this.serviceName === 'narezkavideo') {
        this.successMessage = this.$t('upload.success_clipcut');
      } else if (this.serviceName === 'vid2text') {
        this.successMessage = this.$t('upload.success_vid2text');
      } else {
        this.successMessage = this.$t('upload.success');
      }

      this.uploadProgress = 100;
      this.uploadSuccess = true;
      this.uploading = false;
      this.uploadedMaterialId = data.hash || data.material_id || data.queue_id;
      this.uploadedQueueId = data.queue_id || null;
      this.uploadedQueueToken = data.queue_token || null;

      // Immediately notify parent to refresh materials list
      this.$emit('uploaded', { materialId: this.uploadedMaterialId, queueId: data.queue_id });
    },

    logUploadError(error) {
      try {
        const payload = {
          action: 'chunked_error_log',
          error_message: error.message || String(error),
          upload_id: this.chunkedUploader?.uploadId || null,
          file_name: this.selectedFile?.name || null,
          file_size: this.selectedFile?.size || null,
          progress: this.uploadProgress,
          auto_retry: this.autoRetryCount,
          user_agent: navigator.userAgent
        };
        // Fire-and-forget - don't block on logging
        fetch(`${API_URL}?chatbot_name=${encodeURIComponent(this.serviceName)}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`
          },
          body: JSON.stringify(payload)
        }).catch(() => {}); // Ignore logging failures
      } catch (e) {
        // Ignore logging failures completely
      }
    },

    pauseUpload() {
      if (this.chunkedUploader && this.uploading && !this.isPaused) {
        this.chunkedUploader.pause();
      }
    },

    async resumeUpload() {
      if (this.chunkedUploader && (this.isPaused || this.canResume)) {
        this.errorMessage = '';
        try {
          await this.chunkedUploader.resume();
        } catch (error) {
          // Error handled in onError callback
        }
      }
    },

    abortUpload() {
      if (this.chunkedUploader) {
        this.chunkedUploader.abort();
        this.chunkedUploader = null;
      }
      this.uploading = false;
      this.isPaused = false;
      this.canResume = false;
      this.uploadProgress = 0;
    }
  }
};
</script>

<style scoped>
.upload-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1rem;
}

.upload-modal {
  background: var(--bs-body-bg);
  border-radius: 16px;
  width: 100%;
  max-width: 520px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.upload-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--bs-border-color);
}

.upload-modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--bs-emphasis-color);
}

.close-btn {
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  color: var(--bs-secondary-color);
  border-radius: 8px;
  transition: all 0.2s;
}

.close-btn:hover:not(:disabled) {
  background: var(--bs-tertiary-bg);
  color: var(--bs-emphasis-color);
}

.close-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.upload-modal-body {
  padding: 1.5rem;
}

/* Success State */
.upload-success-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1.5rem;
  text-align: center;
}

.success-icon {
  font-size: 4rem;
  color: #10b981;
  margin-bottom: 1.5rem;
  animation: successPop 0.4s ease-out;
}

@keyframes successPop {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.success-text {
  font-size: 1.1rem;
  font-weight: 500;
  color: var(--bs-emphasis-color);
  max-width: 300px;
}

.drop-zone {
  border: 2px dashed var(--bs-border-color);
  border-radius: 12px;
  padding: 2.5rem 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: var(--bs-tertiary-bg);
}

.drop-zone:hover:not(.drop-zone-disabled) {
  border-color: #6366f1;
  background: rgba(99, 102, 241, 0.05);
}

.drop-zone-active {
  border-color: #6366f1;
  background: rgba(99, 102, 241, 0.1);
  transform: scale(1.02);
}

.drop-zone-disabled {
  cursor: default;
  opacity: 0.9;
}

.drop-zone-has-file {
  border-style: solid;
  border-color: #10b981;
  background: rgba(16, 185, 129, 0.05);
}

.drop-zone-icon {
  font-size: 3rem;
  color: #6366f1;
  margin-bottom: 1rem;
}

.drop-zone-text {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 0.75rem;
}

.drop-zone-main {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--bs-emphasis-color);
}

.drop-zone-hint {
  font-size: 0.9rem;
  color: var(--bs-secondary-color);
}

.drop-zone-formats {
  font-size: 0.8rem;
  color: var(--bs-tertiary-color);
}

/* Selected File */
.selected-file {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem;
}

.file-icon {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
}

.file-info {
  flex: 1;
  text-align: left;
  min-width: 0;
}

.file-name {
  font-weight: 600;
  color: var(--bs-emphasis-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-size {
  font-size: 0.85rem;
  color: var(--bs-secondary-color);
}

.remove-file-btn {
  background: none;
  border: none;
  color: var(--bs-secondary-color);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.2s;
}

.remove-file-btn:hover {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
}

/* Upload Progress */
.upload-progress {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.uploading-file-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.uploading-file-info .file-icon {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.uploading-file-info .file-details {
  flex: 1;
  min-width: 0;
  text-align: left;
}

.uploading-file-info .file-name {
  font-weight: 600;
  color: var(--bs-emphasis-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.uploading-file-info .file-size {
  font-size: 0.85rem;
  color: var(--bs-secondary-color);
}

.progress-section {
  width: 100%;
}

.progress-bar-container {
  width: 100%;
  height: 8px;
  background: var(--bs-border-color);
  border-radius: 4px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #6366f1, #8b5cf6);
  border-radius: 4px;
  transition: width 0.2s ease;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.5rem;
}

.progress-status {
  font-size: 0.85rem;
  color: var(--bs-secondary-color);
}

.progress-percent {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--bs-emphasis-color);
}

/* Form Group */
.form-group {
  margin-top: 1.5rem;
}

.form-group label {
  display: block;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--bs-emphasis-color);
  margin-bottom: 0.5rem;
}

.form-group .form-control {
  border-radius: 10px;
  padding: 0.75rem 1rem;
  border: 1px solid var(--bs-border-color);
  background: var(--bs-body-bg);
  color: var(--bs-body-color);
}

.form-group .form-control:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

/* Messages */
.error-message,
.success-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  margin-top: 1rem;
  font-size: 0.9rem;
}

.error-message {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}

.success-message {
  background: rgba(16, 185, 129, 0.1);
  color: #059669;
}

/* Footer */
.upload-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  border-top: 1px solid var(--bs-border-color);
}

.upload-modal-footer .btn {
  padding: 0.625rem 1.25rem;
  border-radius: 10px;
  font-weight: 500;
}

.upload-modal-footer .btn-primary {
  background: #6366f1;
  border-color: #6366f1;
}

.upload-modal-footer .btn-primary:hover:not(:disabled) {
  background: #4f46e5;
  border-color: #4f46e5;
}

.upload-modal-footer .btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Dark Mode */
html[data-bs-theme="dark"] .upload-modal {
  background: var(--yt-bg-secondary, #1f2937);
}

html[data-bs-theme="dark"] .drop-zone {
  background: var(--yt-bg-tertiary, #374151);
  border-color: var(--yt-border-color, #4b5563);
}

html[data-bs-theme="dark"] .drop-zone:hover:not(.drop-zone-disabled) {
  background: rgba(99, 102, 241, 0.1);
}

html[data-bs-theme="dark"] .form-group .form-control {
  background: var(--yt-bg-tertiary, #374151);
  border-color: var(--yt-border-color, #4b5563);
}

/* Responsive */
@media (max-width: 575px) {
  .upload-modal {
    max-width: none;
    margin: 0.5rem;
    border-radius: 12px;
  }

  .drop-zone {
    padding: 2rem 1rem;
  }

  .upload-modal-footer {
    flex-direction: column;
  }

  .upload-modal-footer .btn {
    width: 100%;
  }
}
</style>
